import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivateBatchChangeRequestsJobDocument,
  type ActivateBatchChangeRequestsJobMutation,
  type ActivateBatchChangeRequestsJobMutationVariables,
  DeactivateBatchChangeRequestsJobDocument,
  type DeactivateBatchChangeRequestsJobMutation,
  type DeactivateBatchChangeRequestsJobMutationVariables,
  GetBatchChangeRequestsJobsDocument,
  type GetBatchChangeRequestsJobsQueryVariables,
  UpdateBatchChangeRequestsJobRunDocument,
  type UpdateBatchChangeRequestsJobRunMutation,
  type UpdateBatchChangeRequestsJobRunMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type UseBatchChangeRequestsJobActionsProps = {
  refetch: () => void;
  evidenceSetId: string;
  canExecute?: () => boolean;
};

export const useBatchChangeRequestsJobActions = ({
  refetch,
  evidenceSetId,
  canExecute,
}: UseBatchChangeRequestsJobActionsProps) => {
  const { t } = useTranslation();
  const actionWithConfirm = useActionWithConfirm();
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const listVariables: GetBatchChangeRequestsJobsQueryVariables = { evidenceSetId };
  const [activateBatchChangeRequestsJob] = useMutation<
    ActivateBatchChangeRequestsJobMutation,
    ActivateBatchChangeRequestsJobMutationVariables
  >(ActivateBatchChangeRequestsJobDocument, {
    refetchQueries: [{ query: GetBatchChangeRequestsJobsDocument, variables: listVariables }],
  });
  const [deactivateBatchChangeRequestsJob] = useMutation<
    DeactivateBatchChangeRequestsJobMutation,
    DeactivateBatchChangeRequestsJobMutationVariables
  >(DeactivateBatchChangeRequestsJobDocument, {
    refetchQueries: [{ query: GetBatchChangeRequestsJobsDocument, variables: listVariables }],
  });
  const [updateJobRun] = useMutation<
    UpdateBatchChangeRequestsJobRunMutation,
    UpdateBatchChangeRequestsJobRunMutationVariables
  >(UpdateBatchChangeRequestsJobRunDocument);
  const onActivateClick = useCallback(
    (job: { id: string; name: string }) => {
      actionWithConfirm({
        confirmMessage: t("ui.activateX", { x: job.name }),
        successMessage: t("feature.common.activatedSuccessfully", { model: "batchChangeRequestsJob" }),
        action: async () => {
          await activateBatchChangeRequestsJob({
            variables: { input: { tenantId, id: job.id } },
          });
        },
        canExecute,
      });
    },
    [actionWithConfirm, t, activateBatchChangeRequestsJob, tenantId, canExecute],
  );

  const onDeactivateClick = useCallback(
    (job: { id: string; name: string }) => {
      actionWithConfirm({
        confirmMessage: t("ui.deactivateX", { x: job.name }),
        successMessage: t("feature.common.deactivatedSuccessfully", { model: "batchChangeRequestsJob" }),
        action: async () => {
          await deactivateBatchChangeRequestsJob({
            variables: { input: { tenantId, id: job.id } },
          });
        },
        canExecute,
      });
    },
    [actionWithConfirm, t, deactivateBatchChangeRequestsJob, tenantId, canExecute],
  );

  const markJobRunAsFailed = useCallback(
    ({ jobRunId }: { jobRunId: string }) => {
      actionWithConfirm({
        confirmMessage: t("feature.batchChangeRequestsJobs.markJobRunAsFailed"),
        successMessage: t("feature.common.updatedSuccessfully", { model: "batchChangeRequestsJobRun" }),
        action: async () => {
          await updateJobRun({
            variables: { input: { tenantId, id: jobRunId, status: "failed" } },
          });
          refetch();
        },
        canExecute,
      });
    },
    [actionWithConfirm, t, updateJobRun, refetch, tenantId, canExecute],
  );

  return { onActivateClick, onDeactivateClick, markJobRunAsFailed };
};
